/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – useSimulator.ts — FINAL
// Engine simulare timp real + buffer istoric live
// ============================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { SensorData, Alert, SensorStatus, HistoricalPoint, SensorHistory } from '../types';
import { mockSensors, mockAlerts, sensorHistory as staticHistory } from '../data/mockData';

const REFRESH_MS = 30_000;
const HISTORY_BUFFER = 120; // max puncte live per senzor

const VARIANCE: Record<string, number> = {
  'Ciprofloxacină':       0.12,
  'Eritromicină':         0.09,
  'Estradiol':            0.06,
  'Organofosfați (OP)':   0.08,
  'Nitrați':              1.80,
  'Pesticide (atrazina)': 0.008,
};

const TREND: Record<string, number> = {
  's01': +0.04, 's02':  0.00, 's03': +0.02, 's04': +0.06,
  's05': +0.03, 's06':  0.00, 's07':  0.00, 's08': +0.01,
  's09':  0.00, 's10':  0.00, 's11': +0.08, 's12':  0.00,
  's13': +0.001,'s14':  0.00, 's15':  0.00,
};

// LOD overrides — modificate din ConfigPanel
let lodOverrides: Record<string, number> = {};
export const setLodOverride = (sensorId: string, lod: number) => {
  lodOverrides[sensorId] = lod;
};
export const getLodOverride = (sensorId: string, defaultLod: number): number =>
  lodOverrides[sensorId] ?? defaultLod;

// Interval override din ConfigPanel
let intervalOverride = REFRESH_MS;
export const setIntervalOverride = (ms: number) => { intervalOverride = ms; };

const fluctuate = (value: number, variance: number, trend: number): number =>
  Math.max(0, parseFloat((value + (Math.random() - 0.48) * variance + trend).toFixed(4)));

const computeStatus = (sensor: SensorData, newVal: number, prevStatus: SensorStatus, lod: number): SensorStatus => {
  if (prevStatus === 'offline')     return Math.random() < 0.05 ? 'online' : 'offline';
  if (prevStatus === 'calibrating') return Math.random() < 0.15 ? 'online' : 'calibrating';
  const r = Math.random();
  if (r < 0.003) return 'offline';
  if (r < 0.008) return 'calibrating';
  return newVal >= lod ? 'warning' : 'online';
};

const maybeAlert = (sensor: SensorData, newVal: number, prevVal: number, lod: number): Alert | null => {
  if (newVal >= lod && prevVal < lod) {
    const severity = newVal >= sensor.maxAllowed * 1.2 ? 'critical'
      : newVal >= sensor.maxAllowed ? 'high'
      : newVal >= lod * 1.5 ? 'medium' : 'low';
    return {
      id: `a-live-${sensor.id}-${Date.now()}`,
      sensorId: sensor.id, sensorName: sensor.name,
      parameter: sensor.parameter, location: sensor.location.label,
      value: newVal, lod, unit: sensor.unit, severity,
      status: 'active',
      message: `${sensor.parameter} a depășit LOD: ${newVal} ${sensor.unit} (LOD: ${lod} ${sensor.unit})`,
      timestamp: new Date().toISOString(),
    };
  }
  return null;
};

// ─── Tipuri exportate ─────────────────────────────────────
export interface SimulatorState {
  sensors:      SensorData[];
  alerts:       Alert[];
  liveHistory:  SensorHistory[];  // buffer istoric live
  lastUpdate:   Date;
  isRunning:    boolean;
  tickCount:    number;
  newAlertIds:  string[];
}

// ─── Inițializare buffer istoric din datele statice ───────
const initLiveHistory = (): SensorHistory[] =>
  staticHistory.map(h => ({
    sensorId: h.sensorId,
    data:     h.data.slice(-50), // ultimele 50 puncte statice ca bază
  }));

// ─── Hook ────────────────────────────────────────────────
export const useSimulator = (intervalMs: number = REFRESH_MS) => {
  const [state, setState] = useState<SimulatorState>({
    sensors:     mockSensors.map(s => ({ ...s })),
    alerts:      [...mockAlerts],
    liveHistory: initLiveHistory(),
    lastUpdate:  new Date(),
    isRunning:   true,
    tickCount:   0,
    newAlertIds: [],
  });

  const stateRef   = useRef(state);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  stateRef.current = state;

  const tick = useCallback(() => {
    setState(prev => {
      const newAlerts: Alert[]  = [];
      const now = new Date().toISOString();

      const updatedSensors = prev.sensors.map(sensor => {
        if (sensor.status === 'offline' && Math.random() > 0.05)
          return { ...sensor, timestamp: now };

        const variance  = VARIANCE[sensor.parameter] ?? 0.1;
        const trend     = TREND[sensor.id] ?? 0;
        const lod       = getLodOverride(sensor.id, sensor.lod);
        const newVal    = fluctuate(sensor.value, variance, trend);
        const newStatus = computeStatus(sensor, newVal, sensor.status, lod);
        const newBat    = Math.max(0,   parseFloat(((sensor.batteryLevel  ?? 50) - (newStatus === 'calibrating' ? 0.3 : 0.1) + Math.random() * 0.05).toFixed(1)));
        const newSig    = Math.min(100, parseFloat(((sensor.signalStrength ?? 80) + (Math.random() - 0.5) * 3).toFixed(0)));

        const alert = maybeAlert(sensor, newVal, sensor.value, lod);
        if (alert) newAlerts.push(alert);

        return {
          ...sensor,
          value: newVal, status: newStatus,
          lod: lod, // reflect LOD override
          batteryLevel: newBat, signalStrength: newSig,
          timestamp: now,
        };
      });

      // Actualizează buffer istoric live
      const updatedHistory = prev.liveHistory.map(h => {
        const sensor = updatedSensors.find(s => s.id === h.sensorId);
        if (!sensor) return h;
        const newPoint: HistoricalPoint = { timestamp: now, value: sensor.value };
        const newData = [...h.data, newPoint].slice(-HISTORY_BUFFER);
        return { ...h, data: newData };
      });

      const allAlerts = [...newAlerts, ...prev.alerts].slice(0, 100);

      return {
        sensors:     updatedSensors,
        alerts:      allAlerts,
        liveHistory: updatedHistory,
        lastUpdate:  new Date(),
        isRunning:   prev.isRunning,
        tickCount:   prev.tickCount + 1,
        newAlertIds: newAlerts.map(a => a.id),
      };
    });
  }, []);

  // Restart interval când se schimbă isRunning sau interval
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (state.isRunning) {
      intervalRef.current = setInterval(tick, intervalOverride || intervalMs);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [state.isRunning, intervalMs, tick]);

  const pause    = () => setState(p => ({ ...p, isRunning: false }));
  const resume   = () => setState(p => ({ ...p, isRunning: true }));
  const forceTick = () => tick();

  return { ...state, pause, resume, forceTick };
};